import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { usageCounts } from '../footage/footageView';
import { TypedMemo } from '../../common/types';
import { useMusicVideoEditor } from '../MusicVideoEditorContext';
import { ClipCard } from '../panels/footage/ClipCard';
import { useAddClip } from '../panels/footage/useAddClip';
import { showcaseMessages } from './showcaseMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import styles from '../MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The clips that the group shows before "All". */
const FIRST_CLIPS = 4;

/**
 * "In this cut · N bank clips" of the Bank tab in showcase mode (design c1, c4): the bank clips that
 * the cut uses, from the footage catalog (no request). They stay when the bank search is busy, and
 * they still play and drag.
 */
const ShowcaseCutClipsComponent: React.FC = () => {
  const intl = useIntl();
  const { footage, doc } = useMusicVideoEditor();
  const { add } = useAddClip();
  const [isAll, setIsAll] = useState(false);
  const uses = useMemo(() => usageCounts(doc.clips), [doc.clips]);
  const inCut = useMemo(() => footage.bank.filter((view) => (uses.get(view.key) ?? 0) > 0), [footage.bank, uses]);
  const shown = useMemo(() => (isAll ? inCut : inCut.slice(0, FIRST_CLIPS)), [inCut, isAll]);
  if (inCut.length === 0) {
    return null;
  }
  return (
    <>
      <div className={styles.sectionHead}>
        <b>{intl.formatMessage(showcaseMessages.inThisCut)}</b>
        <small>{intl.formatMessage(showcaseMessages.inThisCutFacts, { count: inCut.length })}</small>
        <span className={styles.grow} />
        {inCut.length > FIRST_CLIPS && (
          <button type="button" className={styles.linkButton} onClick={() => setIsAll((value) => !value)}>
            {intl.formatMessage(isAll ? showcaseMessages.fewer : showcaseMessages.all)}
          </button>
        )}
      </div>
      <div className={styles.clipGrid}>
        {shown.map((view) => (
          <ClipCard key={view.key} view={view} uses={uses.get(view.key) ?? 0} onPick={add} />
        ))}
      </div>
    </>
  );
};

export const ShowcaseCutClips = TypedMemo(ShowcaseCutClipsComponent);
